import {
  $getSelection, $isNodeSelection, CLICK_COMMAND, COMMAND_PRIORITY_LOW,
  type BaseSelection, type LexicalEditor, type NodeKey
} from 'lexical';
import { mergeRegister } from '@lexical/utils';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useLexicalNodeSelection } from '@lexical/react/useLexicalNodeSelection';
import React, { Suspense, useEffect, useRef, useState } from 'react';
import Resizer from './Resizer';
import { Size } from '../../../lib/Graphics/Dim2';

export type EmbeddedMediaChildrenArgs =
{
    mediaRef: React.RefObject<HTMLElement>;
    style: React.CSSProperties; // { width:'100%', height:'100%', pointerEvents:'none' }
};

export type EmbeddedMediaProperties =
{
    nodeKey: NodeKey;
    initialSize?: Size | null;
    onSizeCommit?: (w: number, h: number) => void;
    children: (args: EmbeddedMediaChildrenArgs) => React.ReactNode;
};

export default function EmbeddedMedia
(
    {
        nodeKey,
        initialSize = null,
        onSizeCommit,
        children,
    }
    : EmbeddedMediaProperties
)
{
    const [editor] = useLexicalComposerContext();
    const [isSelected, setIsSelected, clearSelection] = useLexicalNodeSelection(nodeKey);
    const [isResizing, setIsResizing] = useState(false);
    const [selection, setSelection] = useState<BaseSelection | null>(null);
    const [size, setSize] = useState<Size | null>(initialSize);
    const containerRef = useRef<HTMLDivElement>(null);
    const mediaRef = useRef<HTMLElement>(null);
    const backdropRef = useRef<HTMLDivElement>(null);
    const isFocused = $isNodeSelection(selection) && (isSelected || isResizing);
    useEffect(() =>
    {
        const unregister = editor.registerUpdateListener(({ editorState }) =>
        {
            setSelection(editorState.read(() => $getSelection()));
        });
        const click = editor.registerCommand<MouseEvent>
        (
            CLICK_COMMAND,
            (event) =>
            {
                if (isResizing) { return true; }
                if ((event.target as HTMLElement)?.parentElement === backdropRef.current?.parentElement)
                {
                    if (!event.shiftKey) { clearSelection(); }
                    setIsSelected(!isSelected);
                    return true;
                }
                return false;
            },
            COMMAND_PRIORITY_LOW,
        );
        return mergeRegister(unregister, click);
    },
    [editor, isResizing, isSelected, clearSelection]);
    const handleResizeStart = () =>
    {
        if (mediaRef.current)
        {
            mediaRef.current.style.visibility = 'hidden';
            mediaRef.current.style.pointerEvents = 'none';
        }
        setIsResizing(true);
    };
    const handleResizeEnd = (width: number, height: number) =>
    {
        setIsResizing(false);
        setSize({ width, height });
        if (mediaRef.current)
        {
            mediaRef.current.style.visibility = 'visible';
        }
        onSizeCommit?.(width, height);
    };
    const containerStyle: React.CSSProperties =
    {
        position: 'relative',
        display: 'inline-block',
        width: size?.width,
        height: size?.height,
    };
    const mediaStyle: React.CSSProperties =
    {
        width: '100%',
        height: '100%',
        display: 'block',
        pointerEvents: 'none',
    };
    const backdropClassName = isFocused ? 'backdrop-focused' : 'backdrop';
    const element =
    <Suspense fallback={null}>
    <div ref={containerRef} className="resizable" style={containerStyle}
        draggable={isSelected && $isNodeSelection(selection)}>
        {children({ mediaRef, style: mediaStyle })}
        <div
            ref={backdropRef}
            className={backdropClassName}
            // onClick={() => setIsSelected(!isSelected)}
        />
        {isFocused && (
        <Resizer
            editor={editor as unknown as LexicalEditor}
            targetRef={containerRef}
            onResizeStart={handleResizeStart}
            onResizeEnd={handleResizeEnd}
            showCaption={false}
            setShowCaption={() => {}}
        />
        )}
    </div>
    </Suspense>;
    return element;
}